import {
  ATTENTION_WELL_DOT_GLOW_LAYER,
  ATTENTION_WELL_DOT_LAYER,
  ATTENTION_WELL_JUMP_GLOW_LAYER,
  ATTENTION_WELL_JUMP_LAYER,
  ATTENTION_WELL_LAYER,
  ATTENTION_WELL_POINT_LAYER,
  BASE_WELL_DEFAULT_ZOOM,
  BASE_WELL_LAYER,
  BASE_WELL_RASTER_LAYER,
  COMPLETION_FRAC_LAYER,
  COMPLETION_PERF_LAYER,
  COUNTY_LABEL_LAYER,
  COUNTY_LAYER,
  FACILITY_LABEL_LAYER,
  FACILITY_LAYER,
  FACILITY_TYPE_ENUM,
  FRAC_COLOR,
  FRAC_LAYER,
  FRAC_TYPE_CLAUSE,
  HIGHLIGHT_GLOW_PAINT,
  HIGHLIGHT_LINE_LAYOUT,
  IPDB_CONTROL_WELLS_LAYER,
  IPDB_LAYER,
  RIG_COLOR,
  RIG_FRAC_SOURCE_LAYER,
  RIG_LAYER,
  RIG_TYPE_CLAUSE,
  SELECTED_SINGLE_WELL_GLOW_LAYER,
  SELECTED_SINGLE_WELL_LAYER,
  SELECTED_SINGLE_WELL_POINT_GLOW_LAYER,
  SELECTED_SINGLE_WELL_POINT_LAYER,
  SELECTED_WELL_LAYER,
  SELECTED_WELL_POINT_LAYER,
  STICK_LAYER,
  SYNC_WELL_LAYER,
  TX_ABSTRACT_LABEL_LAYER,
  TX_ABSTRACT_LAYER,
  TX_BLOCK_LABEL_LAYER,
  TX_BLOCK_LAYER,
  TX_SECTION_LABEL_LAYER,
  TX_SECTION_LAYER,
  US_SECTION_LABEL_LAYER,
  US_SECTION_LAYER,
  US_TOWNSHIP_LABEL_LAYER,
  US_TOWNSHIP_LAYER,
  WELL_LABEL_LAYER,
  WELL_LAYER,
  WELL_LAYER_HIGHLIGHTED,
  WELL_LAYER_POINT,
  WELL_LAYER_POINT_HIGHLIGHTED,
  WELL_SOURCE_LAYER,
  XDA_INTERCEPT_LAYER,
  XDA_LABEL,
  XDA_WELL_INTERCEPT_POINT,
  highlightDotRadius,
  highlightGlowRadius,
  highlightLineGlowPaint,
  highlightedWellLineThickness,
  vertWellCircleRadius
} from "constants/mapLayers.constants";
import mapboxgl, { GeoJSONSourceRaw, VectorSource } from "mapbox-gl";

import { absolutizeServiceRoot } from "../utils/absolutizeServiceRoot";
import {
  HOLLOW_DOT_STROKE_PAINT,
  hollowDotColorPaint,
  wellDotSizePaint,
  wellLineDasharray
} from "../utils/baseWellGlyphs";
import {
  BASE_WELL_LINES_LAYER,
  BASE_WELL_PAINT,
  BaseWellStyle,
  resolveBaseWellStyle
} from "../utils/baseWellStyle";
import {
  getBaseWellLayerTileUrl,
  getBaseWellRasterTileUrl,
  getLegacyBaseWellLayerTileUrl
} from "../utils/getBaseWellLayerTileUrl";
import {
  FRAC_OUTLINE_PX_PER_ICON_SIZE,
  RIG_OUTLINE_PX_PER_ICON_SIZE,
  buildRigFracHaloWidthExpression,
  buildRigFracIconSizeExpression
} from "../utils/rigFracIconSizeExpressions";

const mapServiceEndpoint = absolutizeServiceRoot(process.env.REACT_APP_MAP_SERVICE);
const mapRsServiceEndpoint = absolutizeServiceRoot(process.env.REACT_APP_MAP_RS_SERVICE);
const map_server_root = absolutizeServiceRoot(process.env.REACT_APP_MAP_GRID_SERVER);

// Small markers at national zoom, prominent icons at basin/pad zoom. Built from
// the shared helper (nothing hovered, multiplier 1) so this layer-init curve and
// the user/hover icon-size adapter stay in sync — both produce the same resting
// expression.
const RIG_FRAC_ICON_SIZE = buildRigFracIconSizeExpression() as mapboxgl.Expression;

const RIG_HALO_WIDTH = buildRigFracHaloWidthExpression(
  RIG_OUTLINE_PX_PER_ICON_SIZE
) as mapboxgl.Expression;
const FRAC_HALO_WIDTH = buildRigFracHaloWidthExpression(
  FRAC_OUTLINE_PX_PER_ICON_SIZE
) as mapboxgl.Expression;

const emptyFeatureCollectionData = {
  type: "FeatureCollection",
  features: []
};

export const defaultWellLineThickness = 2;

// The highlighted well's black line. The jump overlay resizes its own to the
// well line setting, and the glow scales with it.
const HIGHLIGHT_LINE_WIDTH = 5;

// Shared paint expression for WELL_LAYER and STICK_LAYER so stick appearance
// stays identical to org well lines (no restyle) when sticks move to their own layer.
const wellLinePaint = (thickness: number): mapboxgl.LinePaint => ({
  "line-color": ["case", ["has", "color"], ["get", "color"], ["feature-state", "color"]],
  "line-width": thickness,
  // Constant, so dimming repaints in place; a per-well expression reloads every well tile.
  "line-opacity": 1,
  // Width-compensated so dashes keep a fixed pixel length at any line thickness.
  "line-dasharray": wellLineDasharray(thickness)
});

type LayerWithBefore = mapboxgl.Layer & {
  before?: string;
};

interface MapLayersOptions {
  orgId: string;
  /** The base well layer's own filterId (EVA-35). No filterId → no layers/source. */
  filterId?: string;
  /** Fingerprint of the org's ordered survey preference — see buildSurveyPreferenceToken. */
  prefHash?: string;
  /** Fingerprint of the org's ordered header preference — see buildHeaderPreferenceToken. */
  attrPrefHash?: string;
  hasFeature?: (feature: string) => boolean;
  /**
   * "Base Midstream" FEATURE_FLAGS gate — a hard prerequisite for all
   * midstream/facility functionality. Defaults to `true` (undefined ==
   * enabled) to match FEATURE_FLAGS' own default-true/inverted polarity and
   * to keep existing callers/tests that don't pass it unaffected. When
   * `false`, FACILITY_LAYER and FACILITY_LABEL_LAYER are omitted entirely —
   * every facility-layer mouse-interaction adapter (click/hover/spiderify)
   * already no-ops via `mapbox.getLayer(...)` guards, so removing the layer
   * here is the single point that also disables those interactions.
   */
  hasBaseMidstream?: boolean;
  /** Resolved user style for the base well layers. Unset → defaults. */
  baseWellStyle?: BaseWellStyle;
  /** Saved Line Thickness; creating lines at it avoids a deferred dash rewrite after every style load. */
  wellLineThickness?: number;
  /** "Base Layer Refactor" beta feature (opt-in). Unset/false → legacy GeoServer WMS raster. */
  baseLayerRefactor?: boolean;
  /** Legacy-only inputs. */
  hasSurvey?: boolean;
  hasGdc?: boolean;
  hasIhs?: boolean;
  baseLayerResourcePlays?: readonly string[];
}

export function getMapLayers(options: MapLayersOptions): LayerWithBefore[] {
  const hasBaseMidstream = options.hasBaseMidstream ?? true;
  const baseLayerRefactor = options.baseLayerRefactor ?? false;
  const lineThickness =
    options.wellLineThickness && options.wellLineThickness > 0
      ? options.wellLineThickness
      : defaultWellLineThickness;
  const layers: LayerWithBefore[] = [
    {
      id: "Township",
      type: "line",
      minzoom: 8,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.all_township/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-140.5, 45.2, -94.6, 61.5]
      },
      "source-layer": "grid.all_township",
      paint: {
        "line-color": "#000",
        "line-width": 2
      }
    },
    {
      id: "Section",
      type: "line",
      minzoom: 10,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.all_section/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-139.6, 45.2, -94.6, 60.5]
      },
      "source-layer": "grid.all_section",
      paint: {
        "line-color": "#aaaaaa"
      }
    },
    {
      id: "LSD",
      type: "line",
      minzoom: 12,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.lsd/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-139.5, 47.9, -96.9, 61.5]
      },
      "source-layer": "grid.lsd",
      paint: {
        "line-color": "#aaaaaa"
      }
    },
    {
      id: "LSD Label",
      type: "symbol",
      minzoom: 12,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.lsd_label/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-139.5, 47.9, -96.9, 61.5]
      },
      "source-layer": "grid.lsd_label",
      layout: {
        "text-field": ["get", "label"],
        "text-size": 9,
        "text-allow-overlap": true,
        "text-ignore-placement": true
      },
      paint: {
        "text-color": "#202",
        "text-halo-width": 2
      }
    },
    {
      id: "Section Label",
      type: "symbol",
      minzoom: 10,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.all_section_labels/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-139.6, 45.2, -94.6, 60.5]
      },
      "source-layer": "grid.all_section_labels",
      layout: {
        "text-field": ["get", "legal"],
        "text-size": 12,
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"] // default mapbox fonts
      },
      paint: {
        "text-color": "#202",
        "text-halo-width": 2
      }
    },
    // {
    //   id: Township Label",
    //   type: "symbol",
    //   minzoom: 8,
    //   maxzoom: 22,
    //   source: {
    //     type: "vector",
    //     tiles: [`${map_server_root}/grid.nts_block_point/{z}/{x}/{y}.pbf`]
    //   },
    //   "source-layer": "grid.nts_block_point",
    //   layout: {
    //     "text-field": ["get", "legal"],
    //     "text-size": 9,
    //     "text-allow-overlap": true,
    //     "text-ignore-placement": true
    //   },
    //   paint: {
    //     "text-color": "#202",
    //     "text-halo-width": 2
    //   }
    // },
    {
      id: "NTS Block Label",
      type: "symbol",
      minzoom: 8,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.all_township_label/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-140.4, 45.2, -96.1, 61.5]
      },
      "source-layer": "grid.all_township_label",
      layout: {
        "text-field": ["get", "label"],
        "text-size": 9,
        "text-allow-overlap": true,
        "text-ignore-placement": true
      },
      paint: {
        "text-color": "#202",
        "text-halo-width": 2
      }
    },
    {
      id: COUNTY_LAYER,
      type: "line",
      minzoom: 7,
      maxzoom: 22,
      source: {
        type: "vector",
        // The Aleutian islands cross the antimeridian; a bbox spanning them would
        // cover the globe and prune nothing. Cap at the antimeridian: keeps AK,
        // HI, PR, loses the far-west Aleutians and Pacific territories.
        tiles: [`${map_server_root}/grid.us_county/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-180, 17, -65, 72]
      },
      "source-layer": "grid.us_county",
      paint: {
        "line-color": "#555",
        "line-width": 1.5
      }
    },
    {
      id: US_TOWNSHIP_LAYER,
      type: "line",
      minzoom: 8,
      maxzoom: 22,
      source: {
        type: "vector",
        // PLSS never covered the eastern seaboard; cap east at -75.
        tiles: [`${map_server_root}/grid.us_township/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-180, 24, -75, 72]
      },
      "source-layer": "grid.us_township",
      paint: {
        "line-color": "#999",
        "line-width": 1
      }
    },
    {
      id: TX_BLOCK_LAYER,
      type: "line",
      minzoom: 8,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.tx_block/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-107.2, 25.3, -93, 37]
      },
      "source-layer": "grid.tx_block",
      paint: {
        "line-color": "#999",
        "line-width": 1
      }
    },
    {
      id: US_SECTION_LAYER,
      type: "line",
      minzoom: 10,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.us_section/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-180, 24, -75, 72]
      },
      "source-layer": "grid.us_section",
      paint: {
        "line-color": "#aaaaaa"
      }
    },
    {
      id: TX_SECTION_LAYER,
      type: "line",
      minzoom: 10,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.tx_section/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-107.2, 25.3, -93, 37]
      },
      "source-layer": "grid.tx_section",
      paint: {
        "line-color": "#aaaaaa"
      }
    },
    {
      // Texas abstracts sit almost on top of Texas sections, so this layer comes
      // in one zoom level later and draws dashed to stay readable next to the
      // solid grey section lines.
      id: TX_ABSTRACT_LAYER,
      type: "line",
      minzoom: 11,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.tx_abstract/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-107.2, 25.3, -93, 37]
      },
      "source-layer": "grid.tx_abstract",
      paint: {
        "line-color": "#aaaaaa",
        "line-dasharray": [2, 2]
      }
    },
    {
      id: COUNTY_LABEL_LAYER,
      type: "symbol",
      minzoom: 7,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.us_county_label/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-180, 17, -65, 72]
      },
      "source-layer": "grid.us_county_label",
      layout: {
        "text-field": ["get", "label"],
        "text-size": 12
      },
      paint: {
        "text-color": "#202",
        "text-halo-width": 2
      }
    },
    {
      id: US_TOWNSHIP_LABEL_LAYER,
      type: "symbol",
      minzoom: 8,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.us_township_label/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-180, 24, -75, 72]
      },
      "source-layer": "grid.us_township_label",
      // Size and weight match the Canadian township labels (NTS Block Label).
      layout: {
        "text-field": ["get", "label"],
        "text-size": 9
      },
      paint: {
        "text-color": "#202",
        "text-halo-width": 2
      }
    },
    {
      id: TX_BLOCK_LABEL_LAYER,
      type: "symbol",
      minzoom: 8,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.tx_block_label/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-107.2, 25.3, -93, 37]
      },
      "source-layer": "grid.tx_block_label",
      // Size and weight match the Canadian township labels (NTS Block Label).
      layout: {
        "text-field": ["get", "label"],
        "text-rotate": ["get", "angle"],
        "text-size": 9
      },
      paint: {
        "text-color": "#202",
        "text-halo-width": 2
      }
    },
    {
      id: US_SECTION_LABEL_LAYER,
      type: "symbol",
      minzoom: 11,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.us_section_label/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-180, 24, -75, 72]
      },
      "source-layer": "grid.us_section_label",
      layout: {
        "text-field": ["get", "label"],
        "text-size": 12,
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"]
      },
      paint: {
        "text-color": "#202",
        "text-halo-width": 2
      }
    },
    {
      id: TX_SECTION_LABEL_LAYER,
      type: "symbol",
      minzoom: 11,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.tx_section_label/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-107.2, 25.3, -93, 37]
      },
      "source-layer": "grid.tx_section_label",
      layout: {
        "text-field": ["get", "label"],
        "text-size": 12,
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"]
      },
      paint: {
        "text-color": "#202",
        "text-halo-width": 2
      }
    },
    {
      // Thins abstract-vs-abstract crowding and yields to collision-respecting labels.
      id: TX_ABSTRACT_LABEL_LAYER,
      type: "symbol",
      minzoom: 11,
      maxzoom: 22,
      source: {
        type: "vector",
        tiles: [`${map_server_root}/grid.tx_abstract_label/{z}/{x}/{y}.pbf`],
        maxzoom: 14,
        bounds: [-107.2, 25.3, -93, 37]
      },
      "source-layer": "grid.tx_abstract_label",
      layout: {
        "text-field": ["get", "label"],
        "text-size": 12,
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"]
      },
      paint: {
        "text-color": "#202",
        "text-halo-width": 2
      }
    },

    {
      id: IPDB_LAYER,
      type: "fill",
      source: IPDB_LAYER,
      paint: {
        "fill-color": "transparent",
        "fill-opacity": 0
      },
      layout: {
        visibility: "none"
      }
    },

    {
      id: IPDB_CONTROL_WELLS_LAYER,
      type: "circle",
      source: IPDB_CONTROL_WELLS_LAYER,
      paint: {
        "circle-color": "transparent",
        "circle-radius": 4,
        "circle-opacity": 0
      },
      layout: {
        visibility: "none"
      }
    },
    {
      id: WELL_LAYER,
      type: "line",
      source: {
        type: "vector",
        id: "well-source",
        promoteId: "Uwi",
        tiles: [
          `${mapServiceEndpoint}/well/{z}/{x}/{y}.hz.mvt?txn_id=27e30f3e-ba56-4511-985b-eacfd5517ba4`
        ]
      } as VectorSource,
      "source-layer": "eva-wells",
      paint: wellLinePaint(lineThickness)
    },
    {
      id: STICK_LAYER,
      type: "line",
      source: WELL_LAYER, // string reference — reuses WELL_LAYER's existing vector source, no new tile fetch
      "source-layer": WELL_SOURCE_LAYER, // "eva-wells"
      filter: ["==", ["get", "group"], "stick"],
      paint: wellLinePaint(lineThickness) // same paint as WELL_LAYER — FR6, no restyle
    },
    {
      id: WELL_LAYER_POINT,
      type: "circle",
      source: {
        type: "vector",
        promoteId: "Uwi",
        id: "well-source",
        tiles: [
          `${mapServiceEndpoint}/well/{z}/{x}/{y}.vert.mvt?txn_id=27e30f3e-ba56-4511-985b-eacfd5517ba4`
        ]
      } as VectorSource,
      "source-layer": "eva-wells",
      paint: {
        ...hollowDotColorPaint([
          "case",
          ["has", "color"],
          ["get", "color"],
          ["feature-state", "color"]
        ]),
        ...HOLLOW_DOT_STROKE_PAINT,
        ...wellDotSizePaint(),
        "circle-opacity": 1
      }
    },
    // Filtered, colored and shown by useMapWellHighlighting. On the well sources
    // (like Sticks) so the well tiles are fetched once.
    {
      id: WELL_LAYER_HIGHLIGHTED,
      type: "line",
      source: WELL_LAYER,
      "source-layer": WELL_SOURCE_LAYER,
      filter: ["has", ["get", "Uwi"], ["literal", {}]],
      paint: {
        "line-width": highlightedWellLineThickness(lineThickness),
        "line-dasharray": wellLineDasharray(highlightedWellLineThickness(lineThickness))
      },
      layout: { visibility: "none" }
    },
    {
      id: WELL_LAYER_POINT_HIGHLIGHTED,
      type: "circle",
      source: WELL_LAYER_POINT,
      "source-layer": WELL_SOURCE_LAYER,
      filter: ["has", ["get", "Uwi"], ["literal", {}]],
      paint: {
        ...HOLLOW_DOT_STROKE_PAINT,
        ...wellDotSizePaint()
      },
      layout: { visibility: "none" }
    },
    {
      id: FACILITY_LAYER,
      type: "symbol",
      source: {
        type: "vector",
        id: "facility-source",
        tiles: [`${mapServiceEndpoint}/well/facility/{z}/{x}/{y}?txn_id=clear`]
      } as VectorSource,
      "source-layer": "eva-facility",
      paint: {
        "icon-color": [
          "case",
          ["has", "color"],
          ["get", "color"],
          ["feature-state", "color"]
        ]
      },
      layout: {
        "icon-image": [
          "match",
          ["get", "facilityType"],
          "CompressorStation",
          "compressor-station",
          FACILITY_TYPE_ENUM.GasPlant,
          "gas-plant",
          "Battery",
          "battery",
          "MeteringStation",
          "metering-station",
          "WastePlant",
          "waste-plant",
          "InjectionDisposalFacility",
          "injection-facility",
          "GatheringSystem",
          "gathering-system",
          /* default */ "default-facility"
        ],
        "icon-size": [
          "interpolate",
          ["linear"],
          ["zoom"],
          6,
          0.2,
          10,
          1.0,
          12,
          1.5,
          22,
          2.0
        ],
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-rotate": ["get", "angle"],
        "icon-rotation-alignment": "map",
        "icon-keep-upright": true
      }
    },
    {
      id: COMPLETION_FRAC_LAYER,
      type: "line",
      minzoom: 9,
      maxzoom: 22,
      source: "completion",
      layout: {},
      paint: {
        "line-color": ["match", ["get", "ftype"], "stage", "black", "gray"],
        "line-width": ["match", ["get", "ftype"], "stage", 4, 2]
      }
    },
    {
      id: COMPLETION_PERF_LAYER,
      type: "circle",
      minzoom: 9,
      maxzoom: 22,
      source: "completion",
      layout: {},
      paint: {
        "circle-color": "orange",
        "circle-radius": ["match", ["get", "ftype"], "perfpoint", 2, 0]
      }
    },
    {
      id: SELECTED_WELL_LAYER,
      type: "line",
      source: SELECTED_WELL_LAYER,
      paint: {
        "line-color": ["get", "color"],
        "line-width": 5
      }
    },
    {
      id: SELECTED_WELL_POINT_LAYER,
      type: "circle",
      source: SELECTED_WELL_POINT_LAYER,
      paint: {
        "circle-color": ["get", "color"],
        "circle-radius": {
          base: 2,
          stops: [
            [6, 3],
            [10, 4],
            [15, 50],
            [22, 60]
          ]
        }
      }
    },
    {
      id: SYNC_WELL_LAYER,
      type: "line",
      source: SYNC_WELL_LAYER,
      paint: {
        "line-color": ["get", "color"],
        "line-width": 5
      }
    },
    {
      id: WELL_LABEL_LAYER,
      type: "symbol",
      source: WELL_LABEL_LAYER,
      layout: {
        "text-field": ["get", "value"],
        "text-size": 9,
        "icon-image": ["match", ["get", "wellType"], "Mine", "mine", ""],
        "icon-size": 1,
        "text-variable-anchor": ["top", "bottom", "left", "right"]
      },
      paint: {
        "text-color": "#000",
        "text-halo-color": "#fff",
        "text-halo-width": 2
      }
    },
    {
      id: FACILITY_LABEL_LAYER,
      type: "symbol",
      source: FACILITY_LABEL_LAYER,
      layout: {
        "text-field": ["get", "value"],
        "text-size": 9,
        "icon-size": 1,
        "text-variable-anchor": ["top", "bottom", "left", "right"]
      },
      paint: {
        "text-color": "#000",
        "text-halo-color": "#fff",
        "text-halo-width": 2
      }
    },
    {
      id: XDA_INTERCEPT_LAYER,
      type: "line",
      source: XDA_INTERCEPT_LAYER,
      paint: {
        "line-color": "#000",
        "line-width": 6
      }
    },
    {
      id: XDA_WELL_INTERCEPT_POINT,
      type: "circle",
      source: XDA_INTERCEPT_LAYER,
      paint: {
        "circle-color": "orange",
        "circle-radius": 4
      }
    },
    {
      id: ATTENTION_WELL_LAYER,
      type: "line",
      source: ATTENTION_WELL_LAYER,
      paint: {
        "line-color": "black",
        "line-width": 5
      }
    },
    {
      // A line layer ignores Point geometry, so vertical wells need a circle
      // layer to be drawn at all.
      //
      // Its own source, not the horizontals'. Verticals and horizontals live in
      // separate vector sources upstream, so the overlay keeps them apart the
      // way useContributionHighlight does instead of filtering one shared
      // source by ["geometry-type"] — that filter dropped the verticals, and
      // without it a circle layer draws at EVERY vertex, which put a string of
      // black dots along each horizontal well's own line.
      id: ATTENTION_WELL_POINT_LAYER,
      type: "circle",
      source: ATTENTION_WELL_POINT_LAYER,
      paint: {
        "circle-color": "black",
        // The same size as the vertical well glyph underneath, at every zoom
        // and every dot-thickness setting, so the marker covers the well it
        // marks exactly rather than hiding behind it or ringing it.
        "circle-radius": vertWellCircleRadius() as mapboxgl.Expression
      }
    },
    {
      // Each glow reads its core's source. useManageMapboxLayerSorting pins
      // every glow under every core, above the well layers.
      id: ATTENTION_WELL_JUMP_GLOW_LAYER,
      type: "line",
      source: ATTENTION_WELL_JUMP_LAYER,
      layout: HIGHLIGHT_LINE_LAYOUT,
      paint: highlightLineGlowPaint(HIGHLIGHT_LINE_WIDTH)
    },
    {
      id: ATTENTION_WELL_JUMP_LAYER,
      type: "line",
      source: ATTENTION_WELL_JUMP_LAYER,
      layout: HIGHLIGHT_LINE_LAYOUT,
      paint: {
        "line-color": "black",
        "line-width": HIGHLIGHT_LINE_WIDTH
      }
    },
    {
      id: ATTENTION_WELL_DOT_GLOW_LAYER,
      type: "circle",
      source: ATTENTION_WELL_DOT_LAYER,
      paint: {
        ...HIGHLIGHT_GLOW_PAINT,
        "circle-radius": highlightGlowRadius() as mapboxgl.Expression
      }
    },
    {
      id: ATTENTION_WELL_DOT_LAYER,
      type: "circle",
      source: ATTENTION_WELL_DOT_LAYER,
      paint: {
        "circle-color": "black",
        "circle-radius": highlightDotRadius() as mapboxgl.Expression
      }
    },
    {
      id: XDA_LABEL,
      type: "symbol",
      source: XDA_LABEL,
      layout: {
        "text-field": ["get", "label"],
        "text-size": 20,
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-variable-anchor": ["top"],
        "text-justify": "left"
      },
      paint: {
        "text-color": "#666",
        // The halo's default colour is transparent, so a colourless width
        // bought nothing and the grey A / A' had no edge over the basemap.
        // 2, like every other label layer here: at the old width of 6 the
        // halo read as a solid white pill behind the letters.
        "text-halo-color": "#fff",
        "text-halo-width": 2
      }
    },
    {
      id: SELECTED_SINGLE_WELL_GLOW_LAYER,
      type: "line",
      source: SELECTED_SINGLE_WELL_LAYER,
      layout: HIGHLIGHT_LINE_LAYOUT,
      paint: highlightLineGlowPaint(HIGHLIGHT_LINE_WIDTH)
    },
    {
      id: SELECTED_SINGLE_WELL_LAYER,
      type: "line",
      source: SELECTED_SINGLE_WELL_LAYER,
      layout: HIGHLIGHT_LINE_LAYOUT,
      paint: {
        "line-color": "black",
        "line-width": HIGHLIGHT_LINE_WIDTH
      }
    },
    {
      id: SELECTED_SINGLE_WELL_POINT_GLOW_LAYER,
      type: "circle",
      source: SELECTED_SINGLE_WELL_POINT_LAYER,
      paint: {
        ...HIGHLIGHT_GLOW_PAINT,
        "circle-radius": highlightGlowRadius() as mapboxgl.Expression
      }
    },
    {
      id: SELECTED_SINGLE_WELL_POINT_LAYER,
      type: "circle",
      source: SELECTED_SINGLE_WELL_POINT_LAYER,
      paint: {
        "circle-color": "black",
        "circle-radius": highlightDotRadius() as mapboxgl.Expression
      }
    }
  ];

  // orgId is part of the tile path and is an auth match server-side, so a missing
  // orgId would 403. Gate on it too, and on the endpoint so a missing endpoint
  // never builds an "undefined/rigfrac/..." tile url. Skip the layers otherwise.
  if (
    options.hasFeature?.("Rig And Frac Layers") &&
    options.orgId &&
    mapRsServiceEndpoint
  ) {
    layers.push(
      {
        id: RIG_LAYER,
        type: "symbol",
        minzoom: 2,
        source: {
          type: "vector",
          promoteId: "id",
          tiles: [`${mapRsServiceEndpoint}/rigfrac/${options.orgId}/{z}/{x}/{y}`]
        } as VectorSource,
        "source-layer": RIG_FRAC_SOURCE_LAYER,
        filter: RIG_TYPE_CLAUSE,
        layout: {
          "icon-image": "rig",
          "icon-size": RIG_FRAC_ICON_SIZE,
          "icon-allow-overlap": true,
          "icon-ignore-placement": true
        },
        paint: {
          "icon-color": RIG_COLOR,
          "icon-halo-color": "#000000",
          "icon-halo-width": RIG_HALO_WIDTH
        }
      },
      {
        id: FRAC_LAYER,
        type: "symbol",
        minzoom: 2,
        source: RIG_LAYER, // string reference — reuses the Rigs vector source, no new tile fetch
        "source-layer": RIG_FRAC_SOURCE_LAYER,
        filter: FRAC_TYPE_CLAUSE,
        layout: {
          "icon-image": "frac-crew",
          "icon-size": RIG_FRAC_ICON_SIZE,
          "icon-allow-overlap": true,
          "icon-ignore-placement": true
        },
        paint: {
          "icon-color": FRAC_COLOR,
          "icon-halo-color": "#000000",
          "icon-halo-width": FRAC_HALO_WIDTH
        }
      }
    );
  }

  // The base well layers are declared once the base layer has its own
  // filterId (EVA-35). This used to be gated on `auth.dataAccess`, which
  // eva-user-svc fills from a GDC-only call, so it answers "may this org read
  // GDC data?" — the right question back when the base layer WAS the shared
  // GDC layer, and the wrong one since EVA-35 made it source-agnostic. Gating
  // on it hid the layer from every non-GDC org. Entitlement is the server's
  // call either way: eva-map-svc resolves the org's entitled sources per tile
  // request and eva-data-svc-rs applies per-well access, so an org entitled to
  // nothing gets an empty tile.
  const baseWellStyle = options.baseWellStyle ?? resolveBaseWellStyle(undefined);
  const tileUrl = baseLayerRefactor
    ? getBaseWellLayerTileUrl({
        filterId: options.filterId,
        prefHash: options.prefHash,
        attrPrefHash: options.attrPrefHash
      })
    : undefined;
  // Raster counterpart of the MVT tile above (EVA-35 raster crossover) — same
  // filterId/prefHash contract, so it exists exactly when the MVT tile does.
  // MapboxWrapper's base-well effect is the one that narrows both layers'
  // zoom ranges to HZ_RASTER_MVT_CROSSOVER_ZOOM at runtime (mirroring how the
  // interactive well layer's raster/MVT split is entirely runtime-managed,
  // never baked in here) — this declaration stays unrestricted like the MVT
  // layers below, so a basemap switch that runs this pass before that effect
  // reconciles still shows *something* rather than a zoom-gated gap.
  // Carries attrPrefHash too — see getBaseWellRasterTileUrl (EVA-35 PR #6601
  // round-4 review): the well set it's rendered from varies by header
  // preference even though the raster's pixels don't.
  const rasterTileUrl = baseLayerRefactor
    ? getBaseWellRasterTileUrl({
        filterId: options.filterId,
        prefHash: options.prefHash,
        attrPrefHash: options.attrPrefHash,
        color: baseWellStyle.color
      })
    : undefined;

  // Default range matches the legacy GeoServer layer (11-22). The user's
  // override (displaySettings.layerZoomSettings[BASE_WELL_LAYER]) isn't
  // available here; MapboxWrapper's base-well effect applies it afterwards
  // via setLayerZoomRange on all three ids (useManageMapboxLayerStyles exempts
  // them so it can't fight that pass).
  const zoomSettings = BASE_WELL_DEFAULT_ZOOM;

  // Two disjoint layers, not "wells" vs "the same wells' survey lines":
  // eva-map-svc #596 moved base-tile building onto the interactive well
  // layer's own feature builder, so a well now contributes to exactly one of
  // these two source-layers, never both.
  //   - base_wells (circle, BASE_WELL_LAYER id): ONLY vertical (WellType.V)
  //     wells, at their centroid. In a horizontal play this layer is nearly
  //     empty — it is no longer "one dot per well".
  //   - base_well_lines (`${BASE_WELL_LAYER}-lines`): EVERY horizontal well,
  //     drawn at every zoom (no more server-side minzoom floor), including
  //     one feature per multilateral leg (`group == "leg"`, `legUwi`) —
  //     styled via BASE_WELL_PAINT the same way WELL_LAYER styles its
  //     own legs.
  // Anything that means "the base layer's wells" must read both layers.
  // Plain single-colour circles/lines rather than the interactive well
  // layer's data-driven colour (EVA-35: status glyphs removed for now —
  // placement and vocabulary are under investigation; the well_statu tile
  // attribute still flows on both layers now, only the styling changed). The
  // circle layer carries the BASE_WELL_LAYER id so the layer-tree checkbox
  // and settings keep anchoring to it. Resource-play filtering is
  // server-side (baked into the filter behind tileUrl's `f` query param) —
  // no client-side filter expression needed.
  // Defined here — not only in MapboxWrapper's effect — so style reloads
  // (basemap switches re-run this via useMapLifecycle's style.load handler)
  // restore both layers.
  //
  // No filterId yet (the base layer's first filter post hasn't resolved) →
  // no tileUrl → the source/layers are skipped entirely rather than declared
  // with a URL the server would 400 on.
  if (tileUrl && rasterTileUrl) {
    layers.splice(
      0,
      0,
      {
        id: BASE_WELL_LINES_LAYER,
        type: "line",
        minzoom: zoomSettings.minzoom,
        maxzoom: zoomSettings.maxzoom,
        source: {
          type: "vector",
          id: "base-well-source",
          promoteId: "uwi",
          tiles: [tileUrl]
        } as VectorSource,
        "source-layer": "base_well_lines",
        paint: BASE_WELL_PAINT[BASE_WELL_LINES_LAYER](baseWellStyle)
      },
      {
        id: BASE_WELL_LAYER,
        type: "circle",
        minzoom: zoomSettings.minzoom,
        maxzoom: zoomSettings.maxzoom,
        source: `${BASE_WELL_LAYER}-lines`,
        "source-layer": "base_wells",
        paint: BASE_WELL_PAINT[BASE_WELL_LAYER](baseWellStyle)
      },
      // Raster half of the crossover — single-colour dots from the same
      // renderer as HZ_WELL_RASTER_LAYER. Its zoom range starts unrestricted,
      // same as the two MVT layers above; MapboxWrapper's base-well effect
      // narrows all three at runtime once HZ_RASTER_MVT_CROSSOVER_ZOOM applies.
      {
        id: BASE_WELL_RASTER_LAYER,
        type: "raster",
        minzoom: zoomSettings.minzoom,
        maxzoom: zoomSettings.maxzoom,
        source: {
          type: "raster",
          tiles: [rasterTileUrl],
          tileSize: 256
        },
        paint: BASE_WELL_PAINT[BASE_WELL_RASTER_LAYER](baseWellStyle)
      }
    );
  }

  if (!baseLayerRefactor && (options.hasSurvey || options.hasIhs)) {
    layers.splice(0, 0, {
      id: BASE_WELL_LAYER,
      type: "raster",
      minzoom: BASE_WELL_DEFAULT_ZOOM.minzoom,
      maxzoom: BASE_WELL_DEFAULT_ZOOM.maxzoom,
      source: {
        type: "raster",
        tiles: [
          getLegacyBaseWellLayerTileUrl({
            hasGdc: options.hasGdc,
            hasIhs: options.hasIhs,
            orgId: options.orgId,
            baseLayerResourcePlays: options.baseLayerResourcePlays,
            hasFeature: options.hasFeature
          })
        ],
        tileSize: 256
      },
      paint: {}
    });
  }

  if (!hasBaseMidstream) {
    return layers.filter(
      (layer) => layer.id !== FACILITY_LAYER && layer.id !== FACILITY_LABEL_LAYER
    );
  }

  return layers;
}

export function getMapLayerSources(): {
  [id: string]: mapboxgl.AnySourceData;
} {
  const sources: { [id: string]: mapboxgl.AnySourceData } = {
    completion: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [WELL_LABEL_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [FACILITY_LABEL_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [SELECTED_WELL_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [SELECTED_WELL_POINT_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [SYNC_WELL_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [SELECTED_SINGLE_WELL_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [SELECTED_SINGLE_WELL_POINT_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [XDA_INTERCEPT_LAYER]: {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: {
          type: "GeometryCollection",
          geometries: []
        }
      }
    } as GeoJSONSourceRaw,
    [ATTENTION_WELL_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [ATTENTION_WELL_POINT_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [ATTENTION_WELL_JUMP_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [ATTENTION_WELL_DOT_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [XDA_LABEL]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    // IPDB sources - Start as GeoJSON with empty data
    // Will be replaced with vector tile sources by Ipdb.tsx when enabled
    [IPDB_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw,
    [IPDB_CONTROL_WELLS_LAYER]: {
      type: "geojson",
      data: { ...emptyFeatureCollectionData }
    } as GeoJSONSourceRaw
  };

  return sources;
}
